import { useQuery } from "react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useImmerReducer } from "use-immer";
import * as THREE from "three";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import useProjectContext from "@/services/context/project/use-project-context.ts";

import {
    FileStoreyData,
    ProjectOrganisationContext,
    ProjectOrganisationContextProps
} from "./project-organisation-context-props.ts";
import ProjectOrganisationReducerAction from "./project-organisation-reducer-action.ts";
import { BaamerFileRevisionDto } from "@/models/interfaces/dtos/baamer-file-revision-dto.ts";
import { GetReferencesDto } from "@/models/interfaces/dtos/project-references/get/get-references.ts";
import queryClient from "@/services/data/query.ts";
import OriginDto from "@/models/interfaces/dtos/origin-dto.ts";
import projectOrganisationReducer from "./project-organisation-reducer.ts";
import { fetchItem, postItem } from "@/queries/queries-helper.ts";
import { PROJECT_REFERENCE_ENDPOINTS } from "@/services/endpoints.ts";
import FileType from "@/models/enums/file-type.ts";
import ProjectOrganisation from "./models/project-organisation.ts";
import ProjectOrganisationBuilding from "./models/project-organisation-building.ts";
import ProjectOrganisationStorey from "./models/project-organisation-storey.ts";
import FileData3D from "./models/file-data-3d.ts";
import PlanFileData from "../project-measure/models/plan-file-data.ts";
import KnownScaleContent from "./models/known-scale-content.ts";
import PlanOrigin from "./models/plan-origin.ts";
import { StoreyTypologyType } from "@/models/types/storey-typology-type.ts";
import BaseFileData from "./models/base-file-data.ts";
import SetupFileStoreyData from "./models/setup-file-data.ts";
import ValidationResult from "@/models/types/validation-result.ts";
import { appendRoofsToOrganisation } from "./project-organisation-helper.ts";

const ProjectOrganisationContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { ensureCanEdit } = useProjectEditAccess();
    const { project, updateProjectIds } = useProjectContext();

    const pendingFiles: Map<string, File> = useMemo(() => new Map<string, File>(), []);

    // Données des plans d'étage. Un étage peut avoir plusieurs plans. Voir pour garder éventuellement l'information quand déplacement dans projet/bâtiment.
    const planFilesData: Map<string, Map<string, PlanFileData>> = useMemo(() => new Map<string, Map<string, PlanFileData>>(), []);

    // Données des maquettes 3D
    const _3dFilesData: Map<string, FileData3D> = useMemo(() => new Map<string, FileData3D>(), []);

    const [isSaving, setIsSaving] = useState(false);
    const [baamerFileRevisions, setBaamerFileRevisions] = useState<BaamerFileRevisionDto[] | undefined>(undefined);

    const [data, dispatch] = useImmerReducer<ProjectOrganisation, ProjectOrganisationReducerAction>(
        projectOrganisationReducer,
        new ProjectOrganisation()
    );

    const { data: projectReferences, isLoading } = useQuery(
        ["project-references", project.id],
        async () => fetchItem<GetReferencesDto>(PROJECT_REFERENCE_ENDPOINTS.GET(project.id)),
        { refetchOnWindowFocus: false }
    );

    const prepareOrigins = useCallback((origins: OriginDto[]): { origin: PlanOrigin | undefined, origins: PlanOrigin[] } => {
        let _origin: PlanOrigin | undefined;
        const _origins: PlanOrigin[] = [];

        origins.forEach(o => {
            try {
                const planOrigin = new PlanOrigin();
                planOrigin.setId(o.originId);
                planOrigin.setName(o.name);
                planOrigin.setColor(o.color);
                planOrigin.setIsActive(o.isActive);
                planOrigin.setRelativeId(o.relativeOriginId);
                planOrigin.setPositionFromJSON(JSON.parse(o.position));
                planOrigin.setDirectionFromJSON(JSON.parse(o.direction));

                _origins.push(planOrigin);

                if (o.isActive)
                    _origin = planOrigin;
            }
            catch {
                // Intentionally ignore invalid JSON
            }
        });

        return { origin: _origin, origins: _origins }
    }, []);

    const prepareSitePosition = useCallback((_data: string | undefined): THREE.Vector3 | undefined => {
        if (!_data) return undefined;
        try {
            const parsedData = JSON.parse(_data);
            const sitePosition = parsedData.sitePosition || parsedData.SitePosition;
            if (sitePosition && typeof sitePosition === 'object')
                return new THREE.Vector3(
                    sitePosition.x || sitePosition.X,
                    sitePosition.y || sitePosition.Y, 
                    sitePosition.z || sitePosition.Z
                );
        } catch {
            // Intentionally ignore invalid JSON
        }
        return undefined;
    }, []);

    const populatePlanFilesData = useCallback((fileRevisions: BaamerFileRevisionDto[]) => {
        planFilesData.clear();
        fileRevisions.forEach((bamfr) => {
            const { file } = bamfr.fileRevision;
            bamfr.storeyFileRevisions.forEach(sfr => {
                let planFileData: PlanFileData = new PlanFileData();
                // Gestion des informations supplémentaires de l'étage.
                if (sfr.data) {
                    try {
                        const parsedData = JSON.parse(sfr.data);
                        planFileData = PlanFileData.fromJSON(parsedData);
                    } catch {
                        // Intentionally ignore invalid JSON
                        planFileData.content = new KnownScaleContent();
                        planFileData.content.default = true;
                    }
                } else {
                    planFileData.content = new KnownScaleContent();
                    planFileData.content.default = true;
                }

                // Préparation des origines
                const { origin, origins } = prepareOrigins(bamfr.origins.filter(o => o.baamerFileRevisionId === sfr.baamerFileRevisionId));
                planFileData.origins = origins;
                if (origin)
                    planFileData.origin = origin;

                const fileStoreys = planFilesData.get(sfr.storeyId);
                if (fileStoreys) {
                    fileStoreys.set(file.fileId, planFileData);
                } else {
                    const map = new Map<string, PlanFileData>();
                    map.set(file.fileId, planFileData);
                    planFilesData.set(sfr.storeyId, map);
                }
            });
        });
    }, [planFilesData, prepareOrigins]);

    useEffect(() => {
        if (isLoading || projectReferences == null)
            return;

        const { projectDescription, baamerFileRevisions: fetchedFilesRevisions } = projectReferences;

        const projectOrganisation = new ProjectOrganisation();

        // Étape 1 : construire la hiérarchie bâtiments/étages
        projectDescription.buildings.forEach(b => {
            const building = new ProjectOrganisationBuilding(b.buildingId, b.name, b.index);

            b.storeys.forEach(s => {
                const storey = new ProjectOrganisationStorey(s.storeyId, s.name, s.altimetry, s.storeyTypeKeyName, s.index);
                building.storeys.push(storey);
            })

            projectOrganisation.buildings.push(building);
        })

        // Étape 2 : trier les étages par altimétrie (décroissant)
        projectOrganisation.buildings.forEach(building => {
            building.storeys.sort((a, b) => b.altimetry - a.altimetry);
        })

        // Étape 3 : attribution des fichiers baamer sur le projet/bâtiments/étages
        populatePlanFilesData(fetchedFilesRevisions);

        fetchedFilesRevisions.forEach((bamfr) => {
            const { file, revision, relativePath } = bamfr.fileRevision;

            bamfr.buildingFileRevisions.forEach(bfr => {
                const building = projectOrganisation.getBuilding(bfr.buildingId);
                if (building) {
                    switch (file.fileType) {
                        case FileType.File3D: {
                            const fileData3d = new FileData3D(file.fileId, file.name, file.fileType, revision, relativePath, file.fileMetaObject);
                            // Préparation des origines
                            const { origin, origins } = prepareOrigins(bamfr.origins.filter(o => o.baamerFileRevisionId === bfr.baamerFileRevisionId));
                            fileData3d.origins = origins
                            if (origin)
                                fileData3d.origin = origin;
                            // Préparation sitePosition
                            const sitePosition = prepareSitePosition(bamfr.data);
                            if (sitePosition)
                                fileData3d.sitePosition = sitePosition;
                            _3dFilesData.set(file.fileId, fileData3d);

                            building.addFile(file.fileType, file.fileId, file.name, revision, relativePath, file.fileMetaObject);
                            break;
                        }
                        case FileType.File3DFrom2D:
                            building.setFile3dFrom2d(file.fileId, revision, relativePath, file.fileMetaObject);
                            break;
                        default:
                            building.addFile(file.fileType, file.fileId, file.name, revision, relativePath, file.fileMetaObject);
                            break;
                    }
                }
            });

            bamfr.storeyFileRevisions.forEach(sfr => {
                const building = projectOrganisation.buildings.find(b => b.storeys.some(s => s.id === sfr.storeyId));
                const storey = building?.storeys.find(s => s.id === sfr.storeyId);
                if (storey) {
                    const planFileData = planFilesData.get(storey.id)?.get(file.fileId);
                    storey.addFile(file.fileType, file.fileId, file.name, revision, relativePath, file.fileMetaObject, planFileData?.content);
                }
            });

            const notInStoreys = bamfr.storeyFileRevisions.length === 0;
            const notInBuildings = bamfr.buildingFileRevisions.length === 0;
            if (notInStoreys && notInBuildings) {
                const fileData3d = new FileData3D(file.fileId, file.name, file.fileType, revision, relativePath, file.fileMetaObject);
                // Préparation des origines
                const { origin, origins } = prepareOrigins(bamfr.origins);
                fileData3d.origins = origins
                if (origin)
                    fileData3d.origin = origin;
                // Préparation sitePosition
                const sitePosition = prepareSitePosition(bamfr.data);
                if (sitePosition)
                    fileData3d.sitePosition = sitePosition;
                _3dFilesData.set(file.fileId, fileData3d);

                projectOrganisation.addFile(file.fileType, file.fileId, file.name, revision, relativePath, file.fileMetaObject);
            }
        });

        dispatch({ type: "populate", payload: projectOrganisation });
        setBaamerFileRevisions(fetchedFilesRevisions);
    }, [dispatch, planFilesData, _3dFilesData, isLoading, projectReferences, prepareOrigins, prepareSitePosition, populatePlanFilesData]);

    const getBaamerFileRevisionIdFromStoreyId = useCallback((storeyId: string) => {
        if (baamerFileRevisions == null) return undefined;
        return baamerFileRevisions.find(bfr => bfr.storeyFileRevisions.find(sfr => sfr.storeyId === storeyId))?.baamerFileRevisionId;
    }, [baamerFileRevisions]);

    const getBaamerFileRevisionIdFromFileId = useCallback((fileId: string) => {
        if (baamerFileRevisions == null) return undefined;
        return baamerFileRevisions.find(bfr => bfr.fileRevision.file.fileId === fileId)?.baamerFileRevisionId;
    }, [baamerFileRevisions]);

    const tryAddBuilding = useCallback((name: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        const match = data.buildings.find(b => b.name === name);
        if (match)
            throw new Error("Un bâtiment avec ce nom existe déjà");

        dispatch({ type: 'addBuilding', name });
    }, [ensureCanEdit, data, dispatch]);

    const tryEditBuilding = useCallback((buildingId: string, name: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        const building = data.getBuilding(buildingId);
        if (building == null)
            throw new Error("Bâtiment non trouvé");

        const match = data.buildings.find(b => b.name === name);
        if (match != null && building !== match)
            throw new Error("Un bâtiment avec ce nom existe déjà");

        dispatch({ type: 'editBuilding', buildingId, name });
    }, [ensureCanEdit, data, dispatch]);

    const removeBuilding = useCallback((buildingId: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        const targetBuilding = data.getBuilding(buildingId);
        if (targetBuilding == null)
            return;

        targetBuilding.files3d.forEach((f => {
            dispatch({ type: 'addFile', scope: 'project', fileId: f.id, name: f.name, fileType: FileType.File3D, revision: f.revision, relativePath: f.relativePath, fileMetaObject: f.fileMetaObject });
        }))

        targetBuilding.files2d.forEach((f => {
            dispatch({ type: 'addFile', scope: 'project', fileId: f.id, name: f.name, fileType: FileType.File2D, revision: f.revision, relativePath: f.relativePath, fileMetaObject: f.fileMetaObject });
        }))

        targetBuilding.storeys.forEach((storey) => {
            storey.files3d.forEach((f => {
                dispatch({ type: 'addFile', scope: 'project', fileId: f.id, name: f.name, fileType: FileType.File3D, revision: f.revision, relativePath: f.relativePath, fileMetaObject: f.fileMetaObject });
            }))

            storey.files2d.forEach((f => {
                dispatch({ type: 'addFile', scope: 'project', fileId: f.id, name: f.name, fileType: FileType.File2D, revision: f.revision, relativePath: f.relativePath, fileMetaObject: f.fileMetaObject });
            }))
        })

        dispatch({ type: 'removeBuilding', buildingId });
    }, [ensureCanEdit, data, dispatch]);

    const tryAddStorey = useCallback((buildingId: string, name: string, altimetry: number, storeyType: StoreyTypologyType) => {
        if (!ensureCanEdit()) {
            return;
        }

        const building = data.getBuilding(buildingId);

        if (building == null)
            throw new Error('Bâtiment non trouvé');

        if (building.storeys.find(s => s.name === name)) {
            throw new Error("Un étage avec ce nom existe déjà.");
        }

        if (building.storeys.find(s => s.altimetry === altimetry)) {
            throw new Error("Un étage possède déjà cette altimétrie.");
        }

        dispatch({ type: 'addStorey', buildingId, name, altimetry, storeyType });
    }, [ensureCanEdit, data, dispatch]);

    const tryEditStorey = useCallback((buildingId: string, storeyId: string, newName?: string, newAltimetry?: number, storeyType?: StoreyTypologyType) => {
        if (!ensureCanEdit()) {
            return;
        }

        const building = data.getBuilding(buildingId);
        const storey = building?.getStorey(storeyId);
        if (building == null || storey == null)
            throw new Error("Étage non trouvé");

        if (newName) {
            const match = building.storeys.find(s => s.name === newName);
            if (match != null && match !== storey) {
                throw new Error("Un étage avec ce nom existe déjà.");
            }
        }

        if (newAltimetry != null) {
            const match = building.storeys.find(s => s.altimetry === newAltimetry);
            if (match != null && match !== storey) {
                throw new Error("Un étage possède déjà cette altimétrie.");
            }
        }

        dispatch({ type: 'editStorey', buildingId, storeyId, name: newName, altimetry: newAltimetry, storeyType });
    }, [ensureCanEdit, data, dispatch]);

    const removeStorey = useCallback((buildingId: string, storeyId: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        const targetStorey = data.getStoreyBuilding(buildingId, storeyId);
        if (targetStorey == null)
            return;

        targetStorey.files3d.forEach((f) => {
            dispatch({ type: 'addFile', scope: 'building', buildingId: buildingId, fileId: f.id, name: f.name, fileType: FileType.File3D, revision: f.revision, relativePath: f.relativePath, fileMetaObject: f.fileMetaObject });
        })

        targetStorey.files2d.forEach((f) => {
            dispatch({ type: 'addFile', scope: 'building', buildingId: buildingId, fileId: f.id, name: f.name, fileType: FileType.File2D, revision: f.revision, relativePath: f.relativePath, fileMetaObject: f.fileMetaObject });
        })

        dispatch({ type: 'removeStorey', buildingId, storeyId });
    }, [ensureCanEdit, data, dispatch])

    const isFileNameDuplicated = useCallback((fileName: string): boolean => {
        if (data.files2d.some(f => f.name === fileName)) {
            return true;
        }

        return data.buildings.some(building => {
            if (building.files2d.some(f => f.name === fileName)) {
                return true;
            }

            return building.storeys.some(storey =>
                storey.files2d.some(f => f.name === fileName)
            );
        });

    }, [data]);

    const tryAddPendingFile = useCallback((fileId: string, file: File, scope: "project" | "building" | "storey", buildingId?: string, storeyId?: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        if (isFileNameDuplicated(file.name))
            throw new Error('Un fichier avec le même nom existe déjà');

        pendingFiles.set(fileId, file);
        dispatch({ type: 'addFile', scope, buildingId, storeyId, fileId, name: file.name, fileType: FileType.File2D, revision: 0, relativePath: '', fileMetaObject: '' });
    }, [ensureCanEdit, dispatch, isFileNameDuplicated, pendingFiles]);

    const removePendingFile = useCallback((fileId: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        pendingFiles.delete(fileId);
        const file = data.files2d.find(f => f.id === fileId);
        if (file) {
            dispatch({ type: 'removeFile', fileType: FileType.File2D, scope: 'project', fileId });
        } else {
            data.buildings.some((building) => {
                const find = building.files2d.find(f => f.id === fileId);
                if (find) {
                    dispatch({ type: 'removeFile', fileType: FileType.File2D, scope: 'building', fileId: find.id, buildingId: building.id });
                    return true;
                }

                return building.storeys.some((storey) => {
                    const storeyFind = storey.files2d.find(f => f.id === fileId);
                    if (storeyFind) {
                        dispatch({ type: 'removeFile', fileType: FileType.File2D, scope: 'storey', fileId: storeyFind.id, buildingId: building.id, storeyId: storey.id });
                        return true;
                    }
                    return false;
                });
            });
        }

    }, [ensureCanEdit, pendingFiles, data.files2d, data.buildings, dispatch])

    const addFile = useCallback((scope: "project" | "building" | "storey", fileType: FileType, fileId: string, fileName: string, revision: number, relativePath: string, fileMetaObject: string, buildingId?: string, storeyId?: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        dispatch({ type: 'addFile', fileType, scope, fileId, name: fileName, buildingId, storeyId, revision, relativePath, fileMetaObject });
    }, [ensureCanEdit, dispatch]);

    const getAllPlanFilesData = useCallback(() => {
        if (planFilesData.size === 0) return [];
        const allPlans: Array<FileStoreyData> = [];

        planFilesData.forEach((fileMap, storeyId) => {
            fileMap.forEach((planData, fileId) => {

                const building = data.buildings.find(b => b.storeys.some(s => s.id === storeyId));
                const storey = building?.storeys.find(s => s.id === storeyId);

                const file = storey?.files2d.find(f => f.id === fileId);

                allPlans.push({
                    fileId,
                    fileName: file?.name ?? '',
                    storeyName: storey?.name ?? '',
                    data: planData
                });
            });
        });

        return allPlans;
    }, [planFilesData, data.buildings]);

    const getPlanFileData = useCallback((storeyId: string | undefined, fileId: string | undefined) => {
        if (storeyId == null || fileId == null) return undefined;
        return planFilesData.get(storeyId)?.get(fileId);
    }, [planFilesData]);

    const setPlanFileData = useCallback((storeyId: string, fileId: string, fileData: PlanFileData) => {
        if (!ensureCanEdit()) {
            return;
        }

        const fileStoreys = planFilesData.get(storeyId);
        if (fileStoreys) {
            fileStoreys.set(fileId, fileData);
        } else {
            const map = new Map<string, PlanFileData>();
            map.set(fileId, fileData);
            planFilesData.set(storeyId, map);
        }
        dispatch({ type: 'updatePlanFileData', storeyId: storeyId, fileId: fileId, fileData: fileData });
    }, [ensureCanEdit, planFilesData, dispatch]);
    
    const removePlanFileData = useCallback((storeyId: string, fileId: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        planFilesData.get(storeyId)?.delete(fileId);
    }, [ensureCanEdit, planFilesData]);

    const removeFile = useCallback((scope: "project" | "building" | "storey", fileType: FileType, fileId: string, buildingId?: string, storeyId?: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        dispatch({ type: 'removeFile', fileType, scope, fileId, buildingId, storeyId });
    }, [ensureCanEdit, dispatch]);

    const getAll3dFilesData = useCallback(() => [..._3dFilesData.values()], [_3dFilesData]);

    const canSaveChanges = useCallback((
        projectData: ProjectOrganisation
    ): ValidationResult => {
        if (projectData.buildings.length === 0) {
            return {
                success: false,
                message: "Le projet doit contenir au moins un bâtiment"
            };
        }

        const buildingsErrors: string[] = [];

        projectData.buildings.forEach((building) => {
            if (building.storeys.length < 2) {
                buildingsErrors.push(building.name);
            }
        });

        const totalErrors = buildingsErrors.length;

        if (totalErrors !== 0) {
            return {
                success: false,
                message: `${totalErrors === 1 ? "Le bâtiment" : "Les bâtiments"} ${buildingsErrors.join(", ")} ${totalErrors === 1 ? "doit" : "doivent"} contenir au moins deux étages.`
            };
        }

        return {
            success: true
        };
    }, []);
    
    const appendRoofs = useCallback((projectData: ProjectOrganisation): ProjectOrganisation => {
        if (!ensureCanEdit()) {
            return projectData;
        }

        dispatch({ type: 'appendRoofs' });
        return appendRoofsToOrganisation(projectData);
    }, [ensureCanEdit, dispatch]);

    const saveChanges = useCallback(async (
        projectData: ProjectOrganisation
    ) => {
        if (!ensureCanEdit()) {
            throw new Error("Le projet est en consultation uniquement.");
        }
        setIsSaving(true);
        const formData = new FormData();

        formData.append('fileDatas', JSON.stringify(planFilesData));

        // Gestion fichiers 3DFrom2D
        const dataCopy = structuredClone(projectData);
        dataCopy.buildings.forEach((building) => {
            const file = building.file3dFrom2d;
            if (file)
                building.files3d.push(new BaseFileData(file.id, file.name, FileType.File3DFrom2D, file.revision, file.relativePath, file.fileMetaObject));
        })

        formData.append('Project', JSON.stringify(dataCopy));

        pendingFiles.forEach((value, key) => {
            formData.append('Files', value);
            formData.append('FileIds', key);
        });

        try {
            const result = await postItem<{ projectReference: ProjectOrganisation, baamerFileRevisions: BaamerFileRevisionDto[] }>(
                PROJECT_REFERENCE_ENDPOINTS.SAVE(project.id),
                formData
            );
            if (result) {
                pendingFiles.clear();
                dispatch({ type: 'hydrate', payload: result.projectReference });
                setBaamerFileRevisions(result.baamerFileRevisions);
                populatePlanFilesData(result.baamerFileRevisions);
                await updateProjectIds();
                toast.success('Sauvegarde réussie');
            }
        }
        finally {
            setIsSaving(false);
        }

    }, [ensureCanEdit, planFilesData, pendingFiles, project.id, dispatch, updateProjectIds, populatePlanFilesData]);

    const setupFiles = useCallback((fileStoreyData: SetupFileStoreyData[]) => {
        if (!ensureCanEdit()) {
            return;
        }

        fileStoreyData.forEach((fileStorey) => {
            pendingFiles.set(fileStorey.fileData.id, fileStorey.file);
        });

        dispatch({ type: 'setupFiles', filesData: fileStoreyData, flowMode: project.flowMode });
    }, [ensureCanEdit, dispatch, pendingFiles, project.flowMode]);

    const invalidateProjectReferences = useCallback(() => queryClient.invalidateQueries({ queryKey: ["project-references", project.id] }), [project.id]);

    const projectOrganisationProps: ProjectOrganisationContextProps = {
        isLoading,
        data,
        baamerFileRevisions,
        isDirty: data.isDirty,
        isSaving,

        tryAddBuilding,
        tryEditBuilding,
        removeBuilding,

        tryAddStorey,
        tryEditStorey,
        removeStorey,

        setupFiles,
        addFile,
        removeFile,

        getAllPlanFilesData,
        getPlanFileData,
        setPlanFileData,
        removePlanFileData,

        getAll3dFilesData,

        tryAddPendingFile,
        removePendingFile,

        canSaveChanges,
        appendRoofs,
        saveChanges,

        getBaamerFileRevisionIdFromStoreyId,
        getBaamerFileRevisionIdFromFileId,

        invalidateProjectReferences
    };

    return (
        <ProjectOrganisationContext.Provider value={projectOrganisationProps}>
            {children}
        </ProjectOrganisationContext.Provider>
    )
}

export default ProjectOrganisationContextProvider;
